/**
 * Signup Form Component
 *
 * Handles user registration with email/password and OAuth options.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logger, LogCategory, logValidationError } from '@/utils/logger';
import { useAuthStore } from '@/stores/authStore';
import { Button, Input, Select } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { Mail, Lock, User, AlertCircle, Building2, Phone, CheckCircle2 } from 'lucide-react';
import { isValidEmail } from '@/utils';
import { ROUTES } from '@/constants';

// ============================================================================
// Component
// ============================================================================

export function SignupForm() {
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();

  const { signup, isLoading, error, clearError } = useAuthStore();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    industry: '',
    orgSize: '',
    phoneNumber: '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);

  // Validate form
  const validateForm = (): boolean => {
    Logger.debug(LogCategory.VALIDATION, 'Validating signup form', 'SignupForm');
    const errors: Record<string, string> = {};

    // Personal Information
    if (!formData.name) {
      errors.name = 'Name is required';
      logValidationError('name', formData.name, 'required', 'Name is required', 'SignupForm');
    } else if (formData.name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
      logValidationError('name', formData.name, 'minLength', 'Name too short', 'SignupForm');
    }

    if (!formData.email) {
      errors.email = 'Email is required';
      logValidationError('email', formData.email, 'required', 'Email is required', 'SignupForm');
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
      logValidationError('email', formData.email, 'format', 'Invalid email format', 'SignupForm');
    }

    if (!formData.password) {
      errors.password = 'Password is required';
      logValidationError('password', '***', 'required', 'Password is required', 'SignupForm');
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
      logValidationError('password', '***', 'minLength', 'Password too short', 'SignupForm');
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
      logValidationError('confirmPassword', '***', 'required', 'Confirm password required', 'SignupForm');
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      logValidationError('confirmPassword', '***', 'match', 'Passwords do not match', 'SignupForm');
    }

    // Company Information
    if (!formData.companyName) {
      errors.companyName = 'Company name is required';
      logValidationError('companyName', formData.companyName, 'required', 'Company name is required', 'SignupForm');
    } else if (formData.companyName.length < 2) {
      errors.companyName = 'Company name must be at least 2 characters';
      logValidationError('companyName', formData.companyName, 'minLength', 'Company name too short', 'SignupForm');
    }

    if (!formData.industry) {
      errors.industry = 'Industry is required';
      logValidationError('industry', formData.industry, 'required', 'Industry is required', 'SignupForm');
    }

    if (!formData.orgSize) {
      errors.orgSize = 'Organization size is required';
      logValidationError('orgSize', formData.orgSize, 'required', 'Organization size is required', 'SignupForm');
    }

    if (!formData.phoneNumber) {
      errors.phoneNumber = 'Phone number is required';
      logValidationError('phoneNumber', formData.phoneNumber, 'required', 'Phone number is required', 'SignupForm');
    } else if (formData.phoneNumber.replace(/\D/g, '').length !== 10) {
      errors.phoneNumber = 'Please enter a valid 10-digit phone number';
      logValidationError('phoneNumber', formData.phoneNumber, 'format', 'Invalid phone number format', 'SignupForm');
    }

    setValidationErrors(errors);

    const isValid = Object.keys(errors).length === 0;
    Logger.info(LogCategory.VALIDATION, `Signup form validation: ${isValid ? 'passed' : 'failed'}`, 'SignupForm', {
      errorCount: Object.keys(errors).length,
    });

    return isValid;
  };

  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  // Handle input change
  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    Logger.debug(LogCategory.UI, `Signup form field changed: ${field}`, 'SignupForm');

    const value = field === 'phoneNumber' ? formatPhoneNumber(e.target.value) : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear errors when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    if (error) {
      clearError();
    }
  };

  // Handle select change
  const handleSelectChange = (field: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    Logger.debug(LogCategory.UI, `Signup form select changed: ${field}`, 'SignupForm');
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

    // Clear errors when user selects
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    if (error) {
      clearError();
    }
  };

  // Handle blur for validation
  const handleBlur = (field: string) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    Logger.info(LogCategory.AUTH, 'Signup form submitted', 'SignupForm');

    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      password: true,
      confirmPassword: true,
      companyName: true,
      industry: true,
      orgSize: true,
      phoneNumber: true,
    });

    if (!validateForm()) {
      Logger.warn(LogCategory.AUTH, 'Signup form validation failed', 'SignupForm');
      return;
    }

    try {
      Logger.startTimer('signupAttempt');

      Logger.info(LogCategory.AUTH, 'Creating account with company info', 'SignupForm', {
        companyName: formData.companyName,
        industry: formData.industry,
        orgSize: formData.orgSize,
        phoneNumber: formData.phoneNumber,
      });

      // Call the signup function with proper SignupData structure
      await signup({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        companyName: formData.companyName,
        domain: formData.companyName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-') + '-' + Date.now().toString(36),
        phoneNumber: formData.phoneNumber,
        industry: formData.industry,
        organizationSize: formData.orgSize,
      });

      Logger.endTimer('signupAttempt');

      // Check if signup was successful
      const { isAuthenticated } = useAuthStore.getState();
      if (isAuthenticated) {
        Logger.info(LogCategory.AUTH, 'Signup successful, navigating to dashboard', 'SignupForm');
        showSuccess('Account created!', 'Welcome to RepuCall. Your account has been created successfully.');
        navigate(ROUTES.DASHBOARD);
      } else {
        // Email confirmation required — show inline confirmation state
        Logger.info(LogCategory.AUTH, 'Signup successful, email confirmation required', 'SignupForm');
        setShowEmailConfirmation(true);
      }
    } catch (err) {
      Logger.error(LogCategory.AUTH, 'Signup form submission error', 'SignupForm', undefined, err);
      const message = err instanceof Error ? err.message : 'Please try again or contact support if the problem persists.';
      showError('Signup failed', message);
    }
  };

  if (showEmailConfirmation) {
    return (
      <div className="w-full max-w-md">
        <div className="text-center py-8">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Check your email</h1>
          <p className="text-slate-600 mb-6">
            Check your email to verify your account. Once verified, you can log in.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Didn't receive the email? Check your spam folder or try signing up again.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate(ROUTES.LOGIN)}
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Create your account</h1>
        <p className="mt-2 text-slate-600">
          Get started with RepuCall today
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-danger-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-danger-800">Signup failed</p>
            <p className="text-sm text-danger-700">{error}</p>
          </div>
        </div>
      )}

      {/* Signup Form */}
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* Personal Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">Personal Information</h3>

          <Input
            label="Full name"
            type="text"
            placeholder="John Doe"
            value={formData.name}
            onChange={handleChange('name')}
            onBlur={handleBlur('name')}
            error={touched.name ? validationErrors.name : undefined}
            leftIcon={<User className="h-4 w-4" />}
            autoComplete="name"
            required
            disabled={isLoading}
          />

          <Input
            label="Email address"
            type="email"
            placeholder="you@company.com"
            value={formData.email}
            onChange={handleChange('email')}
            onBlur={handleBlur('email')}
            error={touched.email ? validationErrors.email : undefined}
            leftIcon={<Mail className="h-4 w-4" />}
            autoComplete="email"
            required
            disabled={isLoading}
          />

          <Input
            label="Phone number"
            type="tel"
            placeholder="(555) 123-4567"
            value={formData.phoneNumber}
            onChange={handleChange('phoneNumber')}
            onBlur={handleBlur('phoneNumber')}
            error={touched.phoneNumber ? validationErrors.phoneNumber : undefined}
            leftIcon={<Phone className="h-4 w-4" />}
            autoComplete="tel"
            required
            disabled={isLoading}
          />

          <Input
            label="Password"
            type="password"
            placeholder="Create a strong password (8+ characters)"
            value={formData.password}
            onChange={handleChange('password')}
            onBlur={handleBlur('password')}
            error={touched.password ? validationErrors.password : undefined}
            leftIcon={<Lock className="h-4 w-4" />}
            autoComplete="new-password"
            required
            disabled={isLoading}
          />

          <Input
            label="Confirm password"
            type="password"
            placeholder="Re-enter your password"
            value={formData.confirmPassword}
            onChange={handleChange('confirmPassword')}
            onBlur={handleBlur('confirmPassword')}
            error={touched.confirmPassword ? validationErrors.confirmPassword : undefined}
            leftIcon={<Lock className="h-4 w-4" />}
            autoComplete="new-password"
            required
            disabled={isLoading}
          />
        </div>

        {/* Company Information */}
        <div className="space-y-4 pt-4 border-t border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">Company Information</h3>

          <Input
            label="Company name"
            type="text"
            placeholder="Acme Corporation"
            value={formData.companyName}
            onChange={handleChange('companyName')}
            onBlur={handleBlur('companyName')}
            error={touched.companyName ? validationErrors.companyName : undefined}
            leftIcon={<Building2 className="h-4 w-4" />}
            autoComplete="organization"
            required
            disabled={isLoading}
          />

          <Select
            label="Industry"
            placeholder="Select your industry"
            value={formData.industry}
            onChange={handleSelectChange('industry')}
            onBlur={handleBlur('industry')}
            error={touched.industry ? validationErrors.industry : undefined}
            required
            disabled={isLoading}
            options={[
              { value: '', label: 'Select your industry' },
              { value: 'technology', label: 'Technology' },
              { value: 'finance', label: 'Finance & Banking' },
              { value: 'healthcare', label: 'Healthcare' },
              { value: 'retail', label: 'Retail & E-commerce' },
              { value: 'telecom', label: 'Telecommunications' },
              { value: 'insurance', label: 'Insurance' },
              { value: 'real-estate', label: 'Real Estate' },
              { value: 'education', label: 'Education' },
              { value: 'marketing', label: 'Marketing & Advertising' },
              { value: 'hospitality', label: 'Hospitality & Travel' },
              { value: 'manufacturing', label: 'Manufacturing' },
              { value: 'other', label: 'Other' },
            ]}
          />

          <Select
            label="Organization size"
            placeholder="Select organization size"
            value={formData.orgSize}
            onChange={handleSelectChange('orgSize')}
            onBlur={handleBlur('orgSize')}
            error={touched.orgSize ? validationErrors.orgSize : undefined}
            required
            disabled={isLoading}
            options={[
              { value: '', label: 'Select organization size' },
              { value: '1-10', label: '1-10 employees' },
              { value: '11-50', label: '11-50 employees' },
              { value: '51-200', label: '51-200 employees' },
              { value: '201-500', label: '201-500 employees' },
              { value: '501-1000', label: '501-1,000 employees' },
              { value: '1001-5000', label: '1,001-5,000 employees' },
              { value: '5001+', label: '5,001+ employees' },
            ]}
          />
        </div>

        <div className="text-xs text-slate-500 pt-2">
          By creating an account, you agree to our Terms of Service and Privacy Policy.
        </div>

        <Button
          type="submit"
          fullWidth
          isLoading={isLoading}
          className="mt-6"
        >
          Create account
        </Button>
      </form>
    </div>
  );
}
